import { useState, useEffect, useCallback, useRef } from 'react';
import useApi from '../useApi';
import axiosInstance from '../axiosInstance';

const BASE = '/geo/zones';

// ─── Plain API (exported for use outside hooks) ─────────────────────────────

export const getZones = (params = {}) =>
  axiosInstance.get(BASE, { params }).then((res) => res.data);

export const getZoneById = (id) =>
  axiosInstance.get(`${BASE}/${id}`).then((res) => res.data);

export const createZone = (data) =>
  axiosInstance.post(BASE, data).then((res) => res.data);

export const updateZone = (id, data) =>
  axiosInstance.put(`${BASE}/${id}`, data).then((res) => res.data);

export const deleteZone = (id) =>
  axiosInstance.delete(`${BASE}/${id}`).then((res) => res.data);

export const toggleZoneActive = (id) =>
  axiosInstance.patch(`${BASE}/${id}/toggle-active`).then((res) => res.data);

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * useGetZones - fetch zones list with params
 * @param {Object} params - { page, limit, search, countryId, destinationId, active, sortBy, sortOrder }
 * @returns { data, pagination, loading, error, refetch }
 */
export const useGetZones = (params = {}) => {
  const {
    page = 1,
    limit = 10,
    search = '',
    countryId = '',
    destinationId = '',
    active = '',
    sortBy = 'name',
    sortOrder = 'ASC',
  } = params;

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Guards against out-of-order responses when filters change (see useRegions).
  const reqIdRef = useRef(0);

  const fetchZones = useCallback(async () => {
    const myId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const queryParams = {
        page,
        limit,
        sortBy,
        sortOrder,
      };
      if (search) queryParams.search = search;
      if (countryId) queryParams.countryId = countryId;
      if (destinationId) queryParams.destinationId = destinationId;
      if (active !== '') queryParams.active = active;

      const response = await axiosInstance.get(BASE, { params: queryParams });
      if (myId !== reqIdRef.current) return; // a newer request superseded this one
      const res = response.data;

      if (res.success) {
        setData(res.data || []);
        if (res.pagination) {
          setPagination(res.pagination);
        }
      } else {
        setError(res.message || 'Failed to load zones');
        setData([]);
      }
    } catch (err) {
      if (myId !== reqIdRef.current) return;
      if (err.response) {
        setError(err.response?.data?.message || err.response?.data?.error || `Request failed (${err.response.status})`);
      } else if (err.request) {
        setError('Cannot reach server. Check that the backend is running (e.g. http://localhost:5000).');
      } else {
        setError(err.message || 'Failed to load zones');
      }
      setData([]);
    } finally {
      if (myId === reqIdRef.current) setLoading(false);
    }
  }, [page, limit, search, countryId, destinationId, active, sortBy, sortOrder]);

  useEffect(() => {
    fetchZones();
  }, [fetchZones]);

  const refetch = useCallback(() => {
    fetchZones();
  }, [fetchZones]);

  return { data, pagination, loading, error, refetch };
};

/**
 * useCreateZone - create zone (useApi pattern)
 */
export const useCreateZone = () => {
  return useApi(BASE, { method: 'POST' });
};

/**
 * useUpdateZone - update zone by id
 * @returns { execute(id, payload), loading, error }
 */
export const useUpdateZone = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id, payload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.put(`${BASE}/${id}`, payload);
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update zone';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

/**
 * useDeleteZone - delete zone by id
 */
export const useDeleteZone = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.delete(`${BASE}/${id}`);
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to delete zone';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

/**
 * useToggleZoneActive - toggle active status
 */
export const useToggleZoneActive = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.patch(`${BASE}/${id}/toggle-active`);
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to toggle status';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};
